import * as React from 'react';

import { Messages } from '~src/codegen/Localize';

import { IAdminProfile } from '../models/IAdminProfile';
import { AdminProfileStyles } from '../styles/AdminProfileStyles';
import { AdminProfileActionMenu } from './AdminProfileActionMenu';

export interface AdminProfileListItemProps {
  profile: IAdminProfile;
  isSelected: boolean;
  onClick: (profile: IAdminProfile) => void;
  onEdit?: (profile: IAdminProfile) => void;
  onDelete?: (profile: IAdminProfile) => void;
}

export const AdminProfileListItem: React.FC<AdminProfileListItemProps> = ({
  profile,
  isSelected,
  onClick,
  onEdit,
  onDelete,
}) => {
  const handleClick = React.useCallback(() => {
    onClick(profile);
  }, [onClick, profile]);

  const handleKeyDown = React.useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick(profile);
    }
  }, [onClick, profile]);

  const itemClassName = isSelected
    ? `${AdminProfileStyles.ProfileListItem} ${AdminProfileStyles.ProfileListItemSelected}`
    : AdminProfileStyles.ProfileListItem;

  return (
    <div
      className={itemClassName}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      data-tb-test-id={`admin-profile-item-${profile.id}`}
      // position: relative so the action menu is anchored to this item
      style={AdminProfileStyles.ProfileListItemContainer}
    >
      <div className={AdminProfileStyles.ProfileListItemName}>
        {profile.name}
        {profile.isSystemProfile && (
          <span className={`${AdminProfileStyles.Badge} ${AdminProfileStyles.SystemBadge}`} style={{ marginLeft: 8 }}>
            {Messages.adminProfileHeaderSystemBadge()}
          </span>
        )}
      </div>
      <div className={AdminProfileStyles.ProfileListItemMeta}>
        <span className={AdminProfileStyles.ProfileListItemMetaItem}>
          {profile.permissionCount} {Messages.adminProfileTabPermissions()}
        </span>
        <span className={AdminProfileStyles.ProfileListItemMetaItem}>
          {profile.siteCount} {Messages.sites()}
        </span>
        <span className={AdminProfileStyles.ProfileListItemMetaItem}>
          {profile.userCount} {Messages.users()}
        </span>
      </div>

      {!profile.isSystemProfile && (
        <div style={AdminProfileStyles.ActionMenuContainer}>
          <AdminProfileActionMenu
            profile={profile}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        </div>
      )}
    </div>
  );
};

AdminProfileListItem.displayName = 'AdminProfileListItem';
